import { Box, Button, Popover, Select, Skeleton, Table, Text, Tooltip } from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { DownloadSimpleIcon, PlusIcon, WarningCircleIcon, XIcon } from "@phosphor-icons/react";
import dayjs from "dayjs";
import qs from "qs";
import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import useSwr from "swr";
import { LedgerApi } from "../../apis";
import {
  IAccountsResponse,
  IJournalEntry,
  IJournalEntryDetail,
  IJournalResponse,
  ILedgerCoverage,
  JournalParams,
} from "../../apis/ledger";
import { Page, PageHeader } from "../../components/layout/Page";
import { DateRangeChip, ListFooter, ListToolbar, type ToolbarTab } from "../../components/list";
import sharedListClasses from "../../components/list/list.module.css";
import { Notice } from "../../components/ui/notice";
import { currencyFormat } from "../../utils";
import { message } from "../../utils/Message";
import { JournalEntryDrawer, ReverseConfirmModal } from "./JournalEntryDrawer";
import { dotClass, JOURNAL_TABS, SourcePill, useShortSourceLabel, useTemplateLabel } from "./journal-shared";
import classes from "./journal.module.css";
import { ListStates, resolveListStates } from "../../components/list";

const COLUMN_COUNT = 6;
const SKELETON_ROWS = 8;
const DEFAULT_LIMIT = 50;

type JournalQuery = {
  tab: string;
  from: string | null;
  to: string | null;
  /** Огноог зориуд цэвэрлэсэн — анхдагч сарыг дахин сонгохгүй. */
  noRange: boolean;
  account: string | null;
  q: string;
  page: number;
  limit: number;
};

const YMD = /^\d{4}-\d{2}-\d{2}$/;

function readQuery(params: URLSearchParams): JournalQuery {
  const tab = params.get("tab") ?? "";
  const from = params.get("from");
  const to = params.get("to");
  return {
    tab: (JOURNAL_TABS as readonly string[]).includes(tab) ? tab : "",
    from: from && YMD.test(from) ? from : null,
    to: to && YMD.test(to) ? to : null,
    noRange: params.get("range") === "all",
    account: params.get("account") || null,
    q: params.get("q") || "",
    page: Math.max(1, parseInt(params.get("page") || "1", 10) || 1),
    limit: Math.min(100, Math.max(1, parseInt(params.get("limit") || String(DEFAULT_LIMIT), 10) || DEFAULT_LIMIT)),
  };
}

function writeQuery(q: JournalQuery): URLSearchParams {
  const p = new URLSearchParams();
  if (q.tab) p.set("tab", q.tab);
  if (q.from) p.set("from", q.from);
  if (q.to) p.set("to", q.to);
  if (q.noRange && !q.from && !q.to) p.set("range", "all");
  if (q.account) p.set("account", q.account);
  if (q.q) p.set("q", q.q);
  if (q.page !== 1) p.set("page", String(q.page));
  if (q.limit !== DEFAULT_LIMIT) p.set("limit", String(q.limit));
  return p;
}

const money = (v: number) => currencyFormat(Number(v || 0)).replace(/\s*₮$/, "");

/** «1200 → 4100 · 2300» */
function accountsText(e: IJournalEntry) {
  const dr = (e.accountsDebit ?? []).join(" · ");
  const cr = (e.accountsCredit ?? []).join(" · ");
  return dr || cr ? `${dr} → ${cr}` : "";
}

function accountsTooltip(e: IJournalEntry) {
  const dr = (e.accountsDebitFull ?? []).join(", ");
  const cr = (e.accountsCreditFull ?? []).join(", ");
  return `${dr} → ${cr}`;
}

/** Журнал (§8.3) — бичилтийн жагсаалт, дэлгэрэнгүй, урвуу бичилт. */
export const LedgerJournalPage = () => {
  const { t } = useTranslation(["billing", "common"]);
  const navigate = useNavigate();
  const templateLabel = useTemplateLabel();
  const sourceLabel = useShortSourceLabel();
  const [searchParams, setSearchParams] = useSearchParams();

  const q = useMemo(() => readQuery(searchParams), [searchParams]);

  const patchQuery = useCallback(
    (patch: Partial<JournalQuery>, resetPage = true) => {
      setSearchParams(writeQuery({ ...q, ...patch, ...(resetPage ? { page: 1 } : {}) }), { replace: true });
    },
    [q, setSearchParams],
  );

  // Хайлт URL-ээс тусдаа state: бичих бүрт хүсэлт явуулахгүй.
  const [searchInput, setSearchInput] = useState(q.q);
  const [searchDebounced] = useDebouncedValue(searchInput, 500);
  useEffect(() => {
    if (searchDebounced.trim() !== q.q) patchQuery({ q: searchDebounced.trim() });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchDebounced]);
  useEffect(() => {
    if (q.q !== searchDebounced.trim()) setSearchInput(q.q);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q.q]);

  // Анхдагч огноо: хамгийн сүүлийн бичилттэй сар. Энэ сар гэж сонговол
  // сүүлийн бичилт өмнөх саруудад байх үед хоосон гарна.
  const needsDefault = !q.from && !q.to && !q.noRange;
  const { data: latest, isLoading: latestLoading } = useSwr<IJournalResponse>(
    needsDefault ? "ledger.journal.latest" : null,
    async () => await LedgerApi.getJournal({ page: 1, limit: 1 }),
    { revalidateOnFocus: false },
  );
  const latestDate = latest?.entries[0]?.entryDate;
  const range: [string | null, string | null] = useMemo(() => {
    if (q.from || q.to) return [q.from, q.to];
    if (needsDefault && latestDate) {
      const d = dayjs(latestDate);
      return [d.startOf("month").format("YYYY-MM-DD"), d.endOf("month").format("YYYY-MM-DD")];
    }
    return [null, null];
  }, [q.from, q.to, needsDefault, latestDate]);
  const rangeReady = !needsDefault || !latestLoading;

  // `?entry=` — «Үйл явдал» дэлгэцээс бичилтийг шууд нээх холбоос.
  const [openEntryId, setOpenEntryId] = useState<string | null>(() => searchParams.get("entry"));
  const [confirmReverse, setConfirmReverse] = useState(false);
  const [busy, setBusy] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);

  const baseParams: Omit<JournalParams, "page" | "limit"> = useMemo(
    () => ({
      ...(range[0] ? { dateFrom: range[0] } : {}),
      ...(range[1] ? { dateTo: range[1] } : {}),
      ...(q.tab ? { sourceType: q.tab } : {}),
      ...(q.account ? { accountId: q.account } : {}),
      ...(q.q ? { search: q.q } : {}),
    }),
    [range, q.tab, q.account, q.q],
  );
  const params: JournalParams = { ...baseParams, page: q.page, limit: q.limit };

  const { data, error, isLoading, isValidating, mutate } = useSwr<IJournalResponse>(
    rangeReady ? `ledger.journal?${qs.stringify(params)}` : null,
    async () => await LedgerApi.getJournal(params),
    { shouldRetryOnError: false, revalidateOnFocus: false },
  );

  const { data: coverage } = useSwr<ILedgerCoverage>(
    "ledger.coverage",
    async () => await LedgerApi.getCoverage(),
    { shouldRetryOnError: false, revalidateOnFocus: false },
  );

  const { data: accountsData } = useSwr<IAccountsResponse>(
    "ledger.accounts",
    async () => await LedgerApi.getAccounts(),
    { revalidateOnFocus: false },
  );
  const accounts = useMemo(() => accountsData?.accounts ?? [], [accountsData]);
  const accountLabel = useMemo(() => {
    const found = accounts.find((a) => a.accountId === q.account);
    return found ? `${found.code} · ${found.name}` : null;
  }, [accounts, q.account]);

  const { data: confirmEntry } = useSwr<IJournalEntryDetail>(
    openEntryId && confirmReverse ? `ledger.entry.${openEntryId}` : null,
    async () => await LedgerApi.getEntry(openEntryId!),
    { revalidateOnFocus: false },
  );

  const entries = useMemo(() => data?.entries ?? [], [data]);
  const summary = data?.summary ?? null;
  const total = data?.pagination.total ?? 0;
  const showError = !!error && !isValidating;
  const showSkeleton = !rangeReady || isLoading || (isValidating && !data && !error);
  const ready = !!summary && !showError;
  const dash = t("common:list.dash");
  const hasFilters = !!(q.tab || q.from || q.to || q.account || q.q);

  const allCount = summary ? Object.values(summary.bySource).reduce((s, n) => s + n, 0) : undefined;
  const tabs: ToolbarTab[] = [
    { value: "", label: t("billing:ledger.jrn.tabs.all"), count: ready ? allCount : undefined },
    ...JOURNAL_TABS.map((code) => ({
      value: code,
      label: t(`billing:ledger.jrn.tabs.${code}`),
      count: ready ? (summary!.bySource[code] ?? 0) : undefined,
    })),
  ];

  // Огноогоор бүлэглэнэ — жагсаалт огноо буурах дарааллаар ирдэг.
  const groups = useMemo(() => {
    const out: { date: string; items: IJournalEntry[]; amount: number }[] = [];
    for (const e of entries) {
      const last = out[out.length - 1];
      if (last && last.date === e.entryDate) {
        last.items.push(e);
        last.amount += Number(e.totalDebit);
      } else {
        out.push({ date: e.entryDate, items: [e], amount: Number(e.totalDebit) });
      }
    }
    return out;
  }, [entries]);

  const orderedIds = useMemo(() => entries.map((e) => e.id), [entries]);

  const clearFilters = () => {
    setSearchInput("");
    setSearchParams(new URLSearchParams([["range", "all"]]), { replace: true });
  };

  const exportCsv = async () => {
    try {
      const blob = await LedgerApi.exportJournalCsv(baseParams);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `journal-${range[0] ?? "all"}-${range[1] ?? "all"}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      message.error((err as Error).message || t("billing:ledger.jrn.exportFailed"));
    }
  };

  const reverse = async () => {
    if (!openEntryId) return;
    setBusy(true);
    try {
      const reversal = await LedgerApi.reverseEntry(openEntryId);
      message.success(t("billing:ledger.reversed", { number: reversal.entryNumber }));
      setConfirmReverse(false);
      setOpenEntryId(reversal.id);
      await mutate();
    } catch (err) {
      message.error((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const modalEntry = confirmEntry ?? entries.find((e) => e.id === openEntryId) ?? null;

  const chipClass = (active: boolean) =>
    `${sharedListClasses.filterChip}${active ? ` ${sharedListClasses.filterChipActive}` : ""}`;

  const renderNote = (e: IJournalEntry) => {
    if (e.alert) {
      return (
        <Text span className={`${classes.pill} ${classes.pillAlert}`}>
          {t("billing:ledger.jrn.noteAlert")}
        </Text>
      );
    }
    if (e.status === "REVERSED") {
      return (
        <Text span className={`${classes.pill} ${classes.pillReversed}`}>
          {t("billing:ledger.jrn.noteReversed")}
        </Text>
      );
    }
    if (e.reversalOfId) {
      return (
        <Text span className={`${classes.pill} ${classes.pillReversal}`}>
          {t("billing:ledger.jrn.noteReversal")}
        </Text>
      );
    }
    return null;
  };

  const renderRow = (e: IJournalEntry) => {
    const unbalanced = Number(e.totalDebit) !== Number(e.totalCredit);
    const acc = accountsText(e);
    const template = templateLabel(e.postingTemplate);
    return (
      <Table.Tr
        key={e.id}
        className={`${classes.row} ${e.id === openEntryId ? classes.rowActive : ""} ${e.status === "REVERSED" ? classes.rowReversed : ""}`}
        tabIndex={0}
        role="button"
        aria-label={e.entryNumber}
        onClick={() => setOpenEntryId(e.id)}
        onKeyDown={(ev) => {
          if (ev.key === "Enter" || ev.key === " ") {
            ev.preventDefault();
            setOpenEntryId(e.id);
          }
        }}
      >
        <Table.Td>
          <Text span className={classes.code}>{e.entryNumber}</Text>
        </Table.Td>
        <Table.Td>
          <Box className={classes.stack}>
            <Text span className={classes.desc}>{e.description?.trim() || dash}</Text>
            {template && <Text span className={classes.sub}>{template}</Text>}
          </Box>
        </Table.Td>
        <Table.Td>
          <SourcePill code={e.sourceType} />
        </Table.Td>
        <Table.Td>
          {acc ? (
            <Tooltip label={accountsTooltip(e)} withArrow>
              <Text span className={classes.accounts}>{acc}</Text>
            </Tooltip>
          ) : (
            dash
          )}
        </Table.Td>
        <Table.Td className={classes.amount}>
          {unbalanced && (
            <Tooltip
              label={t("billing:ledger.unbalancedAmount", {
                debit: currencyFormat(e.totalDebit),
                credit: currencyFormat(e.totalCredit),
              })}
              withArrow
            >
              <WarningCircleIcon size={16} weight="bold" className={classes.warnIcon} />
            </Tooltip>
          )}
          {money(e.totalDebit)}
        </Table.Td>
        <Table.Td>{renderNote(e)}</Table.Td>
      </Table.Tr>
    );
  };

  const listStates = resolveListStates(
    <ListStates
      loading={showSkeleton}
      error={showError ? error : undefined}
      onRetry={() => mutate()}
      empty={showError || showSkeleton || entries.length === 0}
      filtered={hasFilters}
      query={q.q}
      onClear={clearFilters}
      columns={COLUMN_COUNT}
      emptyTitle={t("billing:ledger.jrn.empty")}
    />
  );

  const renderBody = () => {
    return groups.map((g) => (
      <Fragment key={g.date}>
        <Table.Tr className={classes.groupRow}>
          <Table.Td colSpan={COLUMN_COUNT}>
            {t("billing:ledger.jrn.group", { date: g.date, count: g.items.length, amount: money(g.amount) })}
          </Table.Td>
        </Table.Tr>
        {g.items.map(renderRow)}
      </Fragment>
    ));
  };

  const hasCoverage =
    !!coverage && coverage.unpostedInvoices + coverage.unpostedPayments + coverage.cancelledNotReversed > 0;

  const bars = summary
    ? [
        ...JOURNAL_TABS.map((code) => ({ code, n: summary.bySource[code] ?? 0 })),
        {
          code: "OTHER",
          n: Object.entries(summary.bySource)
            .filter(([code]) => !(JOURNAL_TABS as readonly string[]).includes(code))
            .reduce((s, [, n]) => s + n, 0),
        },
      ].filter((b) => b.n > 0)
    : [];
  const barTotal = bars.reduce((s, b) => s + b.n, 0);
  const diff = summary ? Math.abs(summary.totalDebit - summary.totalCredit) : 0;
  const rangeHint = range[0] && range[1] ? `${range[0].slice(5)} - ${range[1].slice(5)}` : t("billing:ledger.jrn.kpiInPeriod");

  return (
    <Page>
      <PageHeader
        title={
          <>
            {t("billing:ledger.journalTitle")}
            {ready && <Text span className={classes.titleChip}>{allCount}</Text>}
          </>
        }
        subtitle={t("billing:ledger.journalSubtitle")}
        extra={
          <Button variant="default" leftSection={<DownloadSimpleIcon size={14} />} onClick={exportCsv}>
            {t("billing:ledger.jrn.export")}
          </Button>
        }
      />

      {hasCoverage && coverage && (
        <Notice
          title={t("billing:ledger.jrn.coverageLead")}
          action={
            <Button variant="default" size="xs" onClick={() => navigate("/ledger/ar")}>
              {t("billing:ledger.jrn.coverageAction")}
            </Button>
          }
        >
          {t("billing:ledger.jrn.coverageCounts", {
            invoices: coverage.unpostedInvoices,
            payments: coverage.unpostedPayments,
            cancelled: coverage.cancelledNotReversed,
          })}
          {coverage.firstUnpostedDate && ` ${t("billing:ledger.jrn.coverageFirst", { date: coverage.firstUnpostedDate })}`}
        </Notice>
      )}

      <Box className={classes.kpi} aria-label={t("billing:ledger.jrn.kpiEntries")}>
        <Box className={classes.kpiCell}>
          <Text span className={classes.kpiLabel}>{t("billing:ledger.jrn.kpiEntries")}</Text>
          <Text span className={classes.kpiValue}>{ready ? summary!.entries : dash}</Text>
          <Text span className={classes.kpiHint}>{rangeHint}</Text>
        </Box>
        <Box className={classes.kpiCell}>
          <Text span className={classes.kpiLabel}>{t("billing:ledger.jrn.kpiBalance")}</Text>
          <Box className={classes.kpiValueRow}>
            {ready && summary!.balanced ? (
              <Text span className={classes.kpiValue}>{money(summary!.totalDebit)}</Text>
            ) : (
              <Text span className={classes.kpiValue}>{ready ? money(summary!.totalDebit) : dash}</Text>
            )}
            {ready && (
              <Text span className={summary!.balanced ? classes.badgeOk : classes.badgeBad}>
                {summary!.balanced ? t("billing:ledger.jrn.kpiBalanced") : t("billing:ledger.jrn.kpiUnbalanced")}
              </Text>
            )}
          </Box>
          <Text span className={`${classes.kpiHint} ${ready && !summary!.balanced ? classes.kpiHintBad : ""}`}>
            {ready
              ? summary!.balanced
                ? t("billing:ledger.jrn.kpiDiff", { amount: "0.00" })
                : t("billing:ledger.jrn.kpiDiff", { amount: money(diff) })
              : " "}
          </Text>
        </Box>
        <Box className={classes.kpiCell}>
          <Text span className={classes.kpiLabel}>{t("billing:ledger.jrn.kpiReversals")}</Text>
          <Text span className={`${classes.kpiValue} ${ready && summary!.reversals === 0 ? classes.kpiValueMuted : ""}`}>
            {ready ? summary!.reversals : dash}
          </Text>
          <Text span className={classes.kpiHint}>{t("billing:ledger.jrn.kpiInPeriod")}</Text>
        </Box>
        <Box className={classes.kpiCell}>
          <Text span className={classes.kpiLabel}>{t("billing:ledger.jrn.kpiBySource")}</Text>
          <Box className={classes.sourceBar}>
            {bars.map((b) => (
              <Box
                key={b.code}
                component="span"
                className={dotClass(b.code)}
                style={{ width: `${(b.n / barTotal) * 100}%` }}
              />
            ))}
          </Box>
          <Box className={classes.sourceLegend}>
            {bars.map((b) => (
              <Text span key={b.code} size="xs">
                {b.code === "OTHER" ? t("billing:ledger.jrn.kpiOther") : sourceLabel(b.code)} {b.n}
              </Text>
            ))}
          </Box>
        </Box>
      </Box>

      <Box className={classes.card}>
        <ListToolbar
          tabs={tabs}
          activeTab={q.tab}
          onTabChange={(tab) => patchQuery({ tab })}
          search={{
            value: searchInput,
            onChange: setSearchInput,
            placeholder: t("billing:ledger.jrn.search"),
            w: 220,
          }}
          filterSlot={
            <>
              <DateRangeChip
                label={t("billing:ledger.jrn.chipDate")}
                value={range}
                onChange={([from, to]) => patchQuery({ from, to, noRange: !from && !to })}
              />
              <Popover opened={accountOpen} onChange={setAccountOpen} position="bottom-end" shadow="md" withinPortal>
                <Popover.Target>
                  <Box component="button" type="button" className={chipClass(!!q.account)} onClick={() => setAccountOpen((o) => !o)}>
                    {q.account ? (
                      <>
                        <Text span size="sm" fw={500}>{accountLabel ?? t("billing:ledger.jrn.chipAccount")}</Text>
                        <XIcon
                          size={12}
                          weight="bold"
                          onClick={(ev) => {
                            ev.stopPropagation();
                            patchQuery({ account: null });
                            setAccountOpen(false);
                          }}
                        />
                      </>
                    ) : (
                      <>
                        <PlusIcon size={12} weight="bold" />
                        <Text span size="sm">{t("billing:ledger.jrn.chipAccount")}</Text>
                      </>
                    )}
                  </Box>
                </Popover.Target>
                <Popover.Dropdown>
                  <Select
                    searchable
                    clearable
                    comboboxProps={{ withinPortal: false }}
                    nothingFoundMessage={t("common:noResultsShort")}
                    data={accounts.map((a) => ({ value: a.accountId, label: `${a.code} · ${a.name}` }))}
                    value={q.account}
                    onChange={(id) => {
                      patchQuery({ account: id });
                      setAccountOpen(false);
                    }}
                    placeholder={t("billing:ledger.jrn.accountPick")}
                    w={300}
                  />
                </Popover.Dropdown>
              </Popover>
            </>
          }
        />

        <Box className={classes.wrap}>
          {listStates ?? (
          <Table className={classes.table} withRowBorders={false} horizontalSpacing={0} verticalSpacing={0}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t("billing:ledger.colNumber")}</Table.Th>
                <Table.Th>{t("billing:ledger.colDescription")}</Table.Th>
                <Table.Th>{t("billing:ledger.colSource")}</Table.Th>
                <Table.Th>{t("billing:ledger.jrn.colAccounts")}</Table.Th>
                <Table.Th ta="right">{t("billing:ledger.colAmount")}</Table.Th>
                <Table.Th>{t("billing:ledger.jrn.colNote")}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>{renderBody()}</Table.Tbody>
          </Table>
          )}
        </Box>

        {!showError && total > 0 && (
          <ListFooter
            from={Math.min(total, (q.page - 1) * q.limit + 1)}
            to={Math.min(total, (q.page - 1) * q.limit + entries.length)}
            total={total}
            page={q.page}
            pageSize={q.limit}
            onPageChange={(p) => patchQuery({ page: p }, false)}
            onPageSizeChange={(limit) => patchQuery({ page: 1, limit })}
            pageSizeOptions={["50", "100"]}
          />
        )}
      </Box>

      <JournalEntryDrawer
        entryId={openEntryId}
        orderedIds={orderedIds}
        rangeHint={range}
        suspendKeys={confirmReverse}
        onClose={() => setOpenEntryId(null)}
        onNavigate={setOpenEntryId}
        onRequestReverse={() => setConfirmReverse(true)}
      />

      <ReverseConfirmModal
        opened={confirmReverse}
        onClose={() => setConfirmReverse(false)}
        onConfirm={reverse}
        busy={busy}
        entry={modalEntry}
      />
    </Page>
  );
};

export default LedgerJournalPage;
